/**
 * UploadingOverlay — Spec rev46 → rev49 → rev56 (refonte UX profonde).
 *
 * Overlay affiché au-dessus d'une zone d'upload pendant le transfert.
 *
 * Spec rev56 (2026-08-02) — refonte visuelle complète :
 *   • Icône 44×44 typée selon l'extension (PDF ≠ image ≠ audio…) sur
 *     fond translucide accent (blue Documents / yellow Tâches).
 *   • Nom du fichier en tête (text-sm, blanc, semibold) — poids
 *     visuel supérieur au libellé « Envoi en cours ».
 *   • Libellé « Envoi en cours… » en dessous, uppercase tracking très
 *     serré, couleur accent dimmée (~70%) — status secondaire.
 *   • Pourcentage aligné à droite en tabular-nums accent gras.
 *   • Vraie barre de progression 5 px sous le bloc, dégradé horizontal
 *     de l'accent + glow, animation fluide.
 *   • Carte : bordure `${accent}66` (~40 % opacité) + inner glow subtil
 *     — se détache clairement du fond sombre du drop zone.
 *
 * Usage :
 *   <div className="relative">
 *     <DropZone ... />
 *     <UploadingOverlay
 *       uploading={uploading}
 *       filename={file?.name}
 *       progress={pct}
 *       accent="#F9BC26"   // ou "#2B579A" pour Documents
 *     />
 *   </div>
 */
import {
  Loader2, FileImage, FileText, FileVideo, FileAudio, FileSpreadsheet,
  FileArchive, File as FileIcon,
} from "lucide-react";

/**
 * iconAndTint — retourne à la fois l'icône lucide adaptée et une teinte
 * spécifique par catégorie (souvent différente de l'accent module) qui
 * permet de reconnaître le type de fichier en un coup d'œil.
 */
export function iconAndTint(name = "") {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (/^(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(ext)) return { Icon: FileImage,       tint: "#5DCAA5" }; // vert clair (image)
  if (/^(pdf)$/.test(ext))                                  return { Icon: FileText,        tint: "#EF4444" }; // rouge (PDF, code universel)
  if (/^(mp3|wav|ogg|aac|flac|aiff|m4a)$/.test(ext))        return { Icon: FileAudio,       tint: "#B983E8" }; // mauve (audio)
  if (/^(mp4|mov|avi|mkv|webm|mpg|mpeg|wmv|flv)$/.test(ext))return { Icon: FileVideo,       tint: "#F0997B" }; // orange (vidéo)
  if (/^(csv|xlsx?|numbers|ods)$/.test(ext))                return { Icon: FileSpreadsheet, tint: "#22C55E" }; // vert vif (tableur)
  if (/^(zip|rar|7z|tar|gz|bz2)$/.test(ext))                return { Icon: FileArchive,     tint: "#F5C842" }; // jaune miel (archive)
  if (/^(txt|md|log|json|xml|yaml|yml)$/.test(ext))         return { Icon: FileText,        tint: "#85B7EB" }; // bleu ciel (texte)
  return { Icon: FileIcon, tint: "#9CA3AF" };
}

const shortenFilename = (name = "", max = 42) => {
  if (name.length <= max) return name;
  const dot = name.lastIndexOf(".");
  if (dot > 0 && dot > name.length - 8) {
    const ext = name.slice(dot);
    const base = name.slice(0, dot);
    return `${base.slice(0, max - ext.length - 1)}…${ext}`;
  }
  return `${name.slice(0, max - 1)}…`;
};

export default function UploadingOverlay({
  uploading,
  filename = "",
  progress = null,
  accent = "#2B579A",
  testid = "upload-overlay",
}) {
  if (!uploading) return null;
  const { Icon, tint } = iconAndTint(filename);
  const pctKnown = typeof progress === "number" && !Number.isNaN(progress);
  const clamped = pctKnown ? Math.max(0, Math.min(100, progress)) : null;

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 rounded-md pointer-events-auto backdrop-blur-sm"
      style={{
        background: "rgba(11,11,11,0.82)",
        border: `1px solid ${accent}66`,
        boxShadow: `inset 0 0 0 1px ${accent}22, 0 0 24px ${accent}22`,
      }}
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid={testid}
    >
      <div
        className="flex items-center gap-3 max-w-[420px] w-[80%] rounded-lg px-3 py-2.5"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: `1px solid ${accent}33`,
        }}
      >
        {/* Icône typée 44×44 sur fond translucide accent + badge Loader */}
        <span
          className="relative inline-flex items-center justify-center rounded-lg shrink-0"
          style={{
            width: 44, height: 44,
            background: `${accent}1F`,
            border: `1px solid ${accent}55`,
            color: tint,
          }}
          aria-hidden="true"
        >
          <Icon size={22} strokeWidth={2} />
          <Loader2
            size={14}
            className="animate-spin absolute -bottom-1 -right-1 rounded-full"
            style={{ background: "#1B1B1B", padding: 2, color: accent, width: 18, height: 18 }}
          />
        </span>

        {/* Nom du fichier (poids visuel principal) + status secondaire dessous */}
        <div className="flex-1 min-w-0">
          <div
            className="text-sm text-white font-semibold truncate leading-tight"
            title={filename}
            data-testid={`${testid}-filename`}
          >
            {shortenFilename(filename) || "Fichier en cours d'envoi"}
          </div>
          <div
            className="text-[10px] uppercase tracking-widest font-medium mt-0.5"
            style={{ color: `${accent}B3` /* ~70% opacity accent */ }}
          >
            Envoi en cours…
          </div>
        </div>

        {/* Pourcentage à droite */}
        {pctKnown && (
          <div
            className="shrink-0 text-sm font-bold tabular-nums"
            style={{ color: accent, minWidth: 44, textAlign: "right" }}
            data-testid={`${testid}-progress`}
          >
            {Math.round(clamped)}%
          </div>
        )}
      </div>

      {/* Barre de progression 5 px avec dégradé horizontal et glow */}
      <div
        className="w-[80%] max-w-[420px] h-[5px] rounded-full overflow-hidden relative"
        style={{ background: "#1F1F1F", boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.03)` }}
        role="progressbar"
        aria-valuenow={pctKnown ? Math.round(clamped) : undefined}
      >
        {pctKnown ? (
          <div
            className="h-full transition-all duration-150"
            style={{
              width: `${Math.max(2, clamped)}%`,
              background: `linear-gradient(90deg, ${accent}CC, ${accent}, ${accent}CC)`,
              boxShadow: `0 0 10px ${accent}80`,
            }}
          />
        ) : (
          <div
            className="h-full absolute inset-y-0"
            style={{
              width: "35%",
              background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
              animation: "lb-upload-indeterminate 1.1s linear infinite",
            }}
          />
        )}
      </div>
      <style>{`
        @keyframes lb-upload-indeterminate {
          0%   { left: -35%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
}
